import PropTypes from 'prop-types';
import { useState } from 'react';
import { SIZE_CHART_TEXTS, getSatisfactionGuaranteeUrl } from './SizeChartTable.constants';
import { SizeChartTableStyles } from './SizeChartTable.style';

const SizeChartTable = ({ sizeChartTable, brandLogoUrl, appCtx }) => {
  const [activeTab, setActiveTab] = useState('table');
  const [imageError, setImageError] = useState(false);

  const shouldShowLogo = brandLogoUrl && !imageError && typeof brandLogoUrl === 'string' && brandLogoUrl.trim() !== '';
  const tenant = appCtx?.tenant;
  const satisfactionGuaranteeUrl = getSatisfactionGuaranteeUrl(tenant);

  if (!sizeChartTable || !sizeChartTable.columns || !sizeChartTable.rows) {
    return null;
  }

  const { columns, rows, activeSizeType } = sizeChartTable;
  const columnCount = columns.length;

  const howToSizeStyles = {
    container: {
      fontFamily: "'Lato', sans-serif",
      fontSize: '14px',
      lineHeight: '20px',
      color: '#333333',
      textAlign: 'left',
      margin: '20px 0',
      padding: '0 20px',
      maxWidth: '760px',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    intro: {
      fontWeight: 'bold',
      margin: '0 0 20px 0',
      color: '#333333',
    },
    item: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '20px',
      marginBottom: '24px',
    },
    text: {
      fontWeight: 'normal',
      margin: '0 0 12px 0',
      color: '#333333',
      display: 'flex',
      gap: '8px',
      flex: 1,
    },
    textLast: {
      fontWeight: 'normal',
      margin: 0,
      color: '#333333',
      display: 'flex',
      gap: '8px',
    },
    stepNumber: {
      fontWeight: 'bold',
      flexShrink: 0,
    },
    imageContainer: {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    image: {
      width: 'auto',
      height: '65px',
      objectFit: 'contain',
    },
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const renderHowToSize = () => (
    <div style={howToSizeStyles.container}>
      <div style={howToSizeStyles.intro}>Encuentra tu talla perfecta</div>

      <div style={howToSizeStyles.item}>
        <div style={howToSizeStyles.text}>
          <span style={howToSizeStyles.stepNumber}>1.</span>
          <span>Busca una zapatilla que te quede bien y cómoda.</span>
        </div>
        <div style={howToSizeStyles.imageContainer}>
          <img src="/a/fa/product/static/images/shoe.png" alt="Zapatilla" style={howToSizeStyles.image} />
        </div>
      </div>

      <div style={howToSizeStyles.item}>
        <div style={howToSizeStyles.text}>
          <span style={howToSizeStyles.stepNumber}>2.</span>
          <span>Revisa la etiqueta en su interior que trae la talla en US, UK, EUR y/o centímetros.</span>
        </div>
        <div style={howToSizeStyles.imageContainer}>
          <img src="/a/fa/product/static/images/size-table.png" alt="Tabla de tallas" style={howToSizeStyles.image} />
        </div>
      </div>

      <div style={howToSizeStyles.textLast}>
        <span style={howToSizeStyles.stepNumber}>3.</span>
        <span>
          Los centímetros son la forma más objetiva para comprar. Con tus centímetros o talla en US o UK puedes
          consultar cualquier tabla de tallas de nuestro sitio y revisar cuál es tu talla chilena para cada marca.
        </span>
      </div>
    </div>
  );

  return (
    <div className="size-chart-table-container">
      {shouldShowLogo && (
        <div className="brand-logo-container">
          <img src={brandLogoUrl} alt="Brand logo" className="brand-logo" loading="lazy" onError={handleImageError} />
        </div>
      )}

      <div className="size-chart-tabs">
        <label className="tab-button" htmlFor="tab-table">
          <input
            type="radio"
            id="tab-table"
            name="size-tabs"
            checked={activeTab === 'table'}
            onChange={() => setActiveTab('table')}
          />
          Tabla de tallas
        </label>

        <label className="tab-button" htmlFor="tab-how-to">
          <input
            type="radio"
            id="tab-how-to"
            name="size-tabs"
            checked={activeTab === 'how-to-size'}
            onChange={() => setActiveTab('how-to-size')}
          />
          ¿Cómo medir?
        </label>

        <span className="glider" />
      </div>

      {activeTab === 'table' ? (
        <>
          <div className="size-chart-grid" style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}>
            {columns.map((column) => (
              <div
                key={`header-${column}`}
                className={`grid-header ${column === activeSizeType ? 'active-column' : ''}`}
              >
                {column}
              </div>
            ))}

            {rows.map((row) =>
              columns.map((column, colIndex) => (
                <div
                  key={`${row[columns[0]]}-${column}`}
                  className={`grid-cell ${column === activeSizeType ? 'active-column' : ''} ${
                    colIndex === columnCount - 1 ? 'last-column' : ''
                  } ${rows.indexOf(row) % 2 === 0 ? 'even-row' : 'odd-row'}`}
                >
                  {row[column] || '-'}
                </div>
              ))
            )}
          </div>

          <div className="size-chart-help-text">
            <p className="help-text-intro">{SIZE_CHART_TEXTS.referenceTable}</p>
            <p className="help-text-title">{SIZE_CHART_TEXTS.betweenSizesTitle}</p>
            <p className="help-text-detail">
              {SIZE_CHART_TEXTS.betweenSizesRecommendation}{' '}
              <a href={satisfactionGuaranteeUrl} target="_blank" rel="noopener noreferrer">
                <span className="underlined-satisfaction">{SIZE_CHART_TEXTS.satisfactionGuarantee}</span>
              </a>{' '}
              {SIZE_CHART_TEXTS.returnPolicy}
            </p>
          </div>
        </>
      ) : (
        renderHowToSize()
      )}

      <style jsx>{SizeChartTableStyles}</style>
      <style jsx>{`
        .brand-logo-container {
          max-width: 760px;
          margin: 0 auto 24px;
          text-align: center;
          padding: 0 20px;
          display: flex;
          justify-content: center;
        }

        .brand-logo {
          max-width: 180px;
          max-height: 60px;
          width: auto;
          height: auto;
          object-fit: contain;
        }

        @media (max-width: 768px) {
          .brand-logo-container {
            margin-bottom: 16px;
          }

          .brand-logo {
            max-width: 150px;
            max-height: 80px;
          }
        }

        @media (max-width: 480px) {
          .brand-logo {
            max-width: 120px;
            max-height: 80px;
          }
        }

        @media (max-width: 768px) {
          .size-chart-grid {
            grid-template-columns: repeat(${columnCount}, minmax(${columnCount > 4 ? '50px' : '60px'}, 1fr)) !important;
          }
        }

        @media (max-width: 480px) {
          .size-chart-grid {
            grid-template-columns: repeat(${columnCount}, minmax(${columnCount > 4 ? '40px' : '50px'}, 1fr)) !important;
          }
        }

        @media (max-width: 380px) {
          .size-chart-grid {
            grid-template-columns: repeat(${columnCount}, minmax(35px, 1fr)) !important;
          }
        }
      `}</style>
    </div>
  );
};

SizeChartTable.defaultProps = {
  brandLogoUrl: null,
  appCtx: {},
};

SizeChartTable.propTypes = {
  sizeChartTable: PropTypes.shape({
    activeSizeType: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  brandLogoUrl: PropTypes.string,
  appCtx: PropTypes.object,
};

export default SizeChartTable;
